import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Newspaper, ArrowRight, ExternalLink } from "lucide-react";
import { useCountry } from "@/contexts/CountryContext";
import { useLocality } from "@/contexts/LocalityContext";
import { newsImageFor } from "@/lib/newsFallback";

export default function ThingsToKnowStrip() {
  const [items, setItems] = useState([]);
  const [scope, setScope] = useState("");
  const [loading, setLoading] = useState(true);
  const { code, country } = useCountry();
  const { locality } = useLocality();

  useEffect(() => {
    // Primary: smart mix, scoped by user's locality (state + country) when set.
    // Backend gracefully falls back to country / global if state has no stories.
    const params = { limit: 5 };
    const chosenCountry = (code && country?.name) || locality?.country || null;
    if (chosenCountry) params.country = chosenCountry;
    if (locality?.state) params.state = locality.state;
    http.get("/news", { params })
      .then(async (r) => {
        let list = r.data.items || [];
        let sc = "";
        if (r.data.location_fallback === "dropped_state" && chosenCountry) sc = chosenCountry.toUpperCase();
        else if (r.data.location_fallback === "dropped_city" && locality?.state) sc = locality.state.toUpperCase();
        else if (locality?.state && params.state) sc = `${locality.state.toUpperCase()}${chosenCountry ? ` · ${chosenCountry.toUpperCase()}` : ""}`;
        else if (chosenCountry) sc = chosenCountry.toUpperCase();
        else sc = "WORLDWIDE";
        if (list.length === 0) {
          try {
            const r2 = await http.get("/news", { params: { limit: 5, top25: true } });
            list = r2.data.items || [];
            sc = "WORLDWIDE";
          } catch {}
        }
        setScope(sc);
        setItems(list);
      })
      .finally(() => setLoading(false));
  }, [code, country?.name, locality?.state, locality?.country]);

  // Never hide the section — a lightweight skeleton keeps the layout stable
  // and reassures users that news is being surfaced.
  if (loading) {
    return (
      <section data-testid="ttk-home-strip" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>
          <Newspaper size={13} className="inline mr-1.5 -mt-0.5" />
          THINGS TO KNOW · TODAY
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="rounded-lg border overflow-hidden animate-pulse" style={{ borderColor: "var(--wofy-line)" }}>
              <div className="w-full aspect-[4/3] bg-[var(--wofy-card-bg,#f2ede4)]" />
              <div className="p-3 space-y-2">
                <div className="h-2 w-1/2 rounded bg-[var(--wofy-card-bg,#f2ede4)]" />
                <div className="h-3 w-full rounded bg-[var(--wofy-card-bg,#f2ede4)]" />
                <div className="h-3 w-3/4 rounded bg-[var(--wofy-card-bg,#f2ede4)]" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (items.length === 0) {
    return (
      <section data-testid="ttk-home-strip" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>
          <Newspaper size={13} className="inline mr-1.5 -mt-0.5" />
          THINGS TO KNOW · TODAY
        </div>
        <div className="rounded-lg border p-6 text-sm" style={{ borderColor: "var(--wofy-line)", color: "var(--wofy-text-soft)" }}>
          The daily briefing is refreshing. Please check back in a few minutes — or{" "}
          <Link to="/things-to-know" className="underline" style={{ color: "var(--wofy-primary)" }}>browse the full archive</Link>.
        </div>
      </section>
    );
  }

  return (
    <section data-testid="ttk-home-strip" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>
            <Newspaper size={13} className="inline mr-1.5 -mt-0.5" />
            THINGS TO KNOW · TODAY{scope ? ` · ${scope}` : ""}
          </div>
          <h2 className="font-display text-3xl md:text-4xl leading-tight">Your daily briefing.</h2>
        </div>
        <Link
          data-testid="ttk-strip-view-all"
          to="/things-to-know"
          className="inline-flex items-center gap-1 text-sm font-semibold hover:underline"
          style={{ color: "var(--wofy-primary)" }}
        >
          View all 25 <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {items.map((a) => (
          <article
            key={a.id}
            data-testid={`ttk-strip-card-${a.id}`}
            className="rounded-lg overflow-hidden border flex flex-col"
            style={{ borderColor: "var(--wofy-line)" }}
          >
            <a href={a.url} target="_blank" rel="noopener noreferrer" className="block">
              <div className="w-full aspect-[4/3] bg-[var(--wofy-card-bg,#f2ede4)] flex items-center justify-center overflow-hidden">
                <img
                  src={newsImageFor(a)}
                  alt=""
                  className="w-full h-full object-cover"
                  data-testid={`ttk-strip-img-${a.id}`}
                  onError={(e) => {
                    // final safety net — if even the fallback SVG fails, drop to the default
                    if (!e.currentTarget.dataset.fallback) {
                      e.currentTarget.dataset.fallback = "1";
                      e.currentTarget.src = "/news-fallbacks/default.svg";
                    }
                  }}
                />
              </div>
            </a>
            <div className="p-3 flex flex-col flex-1">
              <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--wofy-primary)" }}>
                {a.category} · {a.source_name}
              </div>
              <Link to={`/things-to-know/${a.id}`}>
                <h3 className="font-display text-base leading-snug mb-2 hover:underline">{a.title}</h3>
              </Link>
              <p className="text-xs leading-relaxed mb-3 line-clamp-4" style={{ color: "var(--wofy-text-soft)" }}>
                {a.ai_summary}
              </p>
              <a
                href={a.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto inline-flex items-center gap-1 text-xs font-semibold"
                style={{ color: "var(--wofy-primary)" }}
              >
                Read Original <ExternalLink size={11} />
              </a>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
